import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { FormService } from 'src/app/services/form.service';
import { ApiService, Countries } from "../../services/api.service";
import { filter, fromEvent, Subscription, take, debounceTime, distinctUntilChanged, switchMap, startWith } from "rxjs";
import { TrackingService } from "../../services/tracking.service";
import { AutofillMonitor } from "@angular/cdk/text-field";
import { GooglePlacesService } from "../../services/google-places.service";
import { Observable } from 'rxjs';

@Component( {
	selector: 'app-moving-from',
	templateUrl: './moving-from.component.html',
	styleUrls: ['./moving-from.component.scss'],
} )
export class MovingFromComponent implements OnInit {

	movingFrom !: UntypedFormGroup
	formGroupName: string = 'movingFrom'
	public countries!: Countries;
	private autofillSubscription: Subscription | undefined;
	postcodeEnabled = true;
	@ViewChild( 'addressInputEl' ) addressInputEl!: ElementRef;

	labelAddress: string = '';
	labelPostcode: string = '';
	fieldOrder: any = [];
	postcodeBusy = false;
	stateFieldEnabled = false;
	
	// Autocomplete properties
	autocompleteSuggestions$: Observable<any[]> = new Observable();
	autocompleteEnabled = false;
	states = [
		{ code: 'AL', name: 'Alabama' },
		{ code: 'AK', name: 'Alaska' },
		{ code: 'AZ', name: 'Arizona' },
		{ code: 'AR', name: 'Arkansas' },
		{ code: 'CA', name: 'California' },
		{ code: 'CO', name: 'Colorado' },
		{ code: 'CT', name: 'Connecticut' },
		{ code: 'DE', name: 'Delaware' },
		{ code: 'FL', name: 'Florida' },
		{ code: 'GA', name: 'Georgia' },
		{ code: 'HI', name: 'Hawaii' },
		{ code: 'ID', name: 'Idaho' },
		{ code: 'IL', name: 'Illinois' },
		{ code: 'IN', name: 'Indiana' },
		{ code: 'IA', name: 'Iowa' },
		{ code: 'KS', name: 'Kansas' },
		{ code: 'KY', name: 'Kentucky' },
		{ code: 'LA', name: 'Louisiana' },
		{ code: 'ME', name: 'Maine' },
		{ code: 'MD', name: 'Maryland' },
		{ code: 'MA', name: 'Massachusetts' },
		{ code: 'MI', name: 'Michigan' },
		{ code: 'MN', name: 'Minnesota' },
		{ code: 'MS', name: 'Mississippi' },
		{ code: 'MO', name: 'Missouri' },
		{ code: 'MT', name: 'Montana' },
		{ code: 'NE', name: 'Nebraska' },
		{ code: 'NV', name: 'Nevada' },
		{ code: 'NH', name: 'New Hampshire' },
		{ code: 'NJ', name: 'New Jersey' },
		{ code: 'NM', name: 'New Mexico' },
		{ code: 'NY', name: 'New York' },
		{ code: 'NC', name: 'North Carolina' },
		{ code: 'ND', name: 'North Dakota' },
		{ code: 'OH', name: 'Ohio' },
		{ code: 'OK', name: 'Oklahoma' },
		{ code: 'OR', name: 'Oregon' },
		{ code: 'PA', name: 'Pennsylvania' },
		{ code: 'RI', name: 'Rhode Island' },
		{ code: 'SC', name: 'South Carolina' },
		{ code: 'SD', name: 'South Dakota' },
		{ code: 'TN', name: 'Tennessee' },
		{ code: 'TX', name: 'Texas' },
		{ code: 'UT', name: 'Utah' },
		{ code: 'VT', name: 'Vermont' },
		{ code: 'VA', name: 'Virginia' },
		{ code: 'WA', name: 'Washington' },
		{ code: 'WV', name: 'West Virginia' },
		{ code: 'WI', name: 'Wisconsin' },
		{ code: 'WY', name: 'Wyoming' }
	];

	// Initially true because this is the first step anyway
	enableFields = true;

	constructor(
		public formService: FormService,
		private apiService: ApiService,
		private trackingService: TrackingService,
		private autofillMonitor: AutofillMonitor,
		private googlePlacesService: GooglePlacesService
	) {
	}

	ngOnInit(): void {

		// formService comes from constructor
		this.movingFrom = this.formService.mainForm.get( this.formGroupName ) as UntypedFormGroup;

		this.labelAddress = this.formService.getVariationTranslation( 'label-address');
		this.labelPostcode = this.formService.getVariationTranslation( 'label-postcode');
		this.fieldOrder = this.formService.getFieldOrder();

		// Check if state field should be enabled
		this.stateFieldEnabled = this.formService.getSetting('stateFieldEnabled') === true;

		// Check if we can do the postcode api for this site
		// This checks if we only have a postcode from step0
		if( this.formService.verifyPostcodeApi() ) {

			// Launch postcode based on postcode from step0
			this.doPostcodeApi();
		}

		// Initial check
		this.onCountryChange( true );
		
		// Set initial state field validation
		this.updateStateFieldValidation();

		this.formService.stepChange$.subscribe( currentFormGroupName => {

			// This enables the fields in the current step
			this.enableFields = currentFormGroupName === this.formGroupName;
		} );
	}

	ngAfterViewInit() {

		// Check if we need to focus the address first
		if( this.movingFrom.controls['_initiallyFocusAddress'].value ) {

			setTimeout( () => {
				this.addressInputEl.nativeElement.focus();
			}, 0 )
		}

		// Initialize Google Places Autocomplete if enabled
		this.initializeGooglePlacesAutocomplete();

		this.autofillSubscription = this.autofillMonitor.monitor( document.body ).subscribe(event => {

			// Subscribe to input event to check for value changes after autofill
			const inputElement = event.target as HTMLInputElement;

			fromEvent(inputElement, 'input').pipe(
				take(1),
				filter(() => !!inputElement.value.trim() )
			).subscribe(() => {

				console.log( 'Autofill detected' );

				// Perform your tracking logic here
				this.trackingService.sendGTMEvent( {
					'event': 'autofill detected',
					'eventName': 'autofill',
				} );

				// Unsubscribe from the autofill monitor after the first detection
				// This also eliminates the inner subscribe
				if( this.autofillSubscription ) {
					this.autofillSubscription.unsubscribe();
				}
			} );
		} );
	}

	/**
	 * Initialize Google Places Autocomplete for this component
	 */
	private initializeGooglePlacesAutocomplete(): void {

		// Only disable Google Places Autocomplete if explicitly set to false
		if (this.formService.domainData?.enableGooglePlacesAutocomplete === false) {
			return;
		}

		// Get country code for restriction
		const countryCode = this.movingFrom.get('country')?.value;

		if( !countryCode ) {
			return;
		}

		// Initialize autocomplete using the service
		this.autocompleteSuggestions$ = this.googlePlacesService.initializeAutocomplete(
			this.movingFrom,
			'address',
			countryCode
		);
	}

	/**
	 * Display function for autocomplete - shows the user's typed input
	 */
	displayWithTyped = (): string => this.movingFrom.get('address')?.value || '';

	/**
	 * Handle autocomplete suggestion selection
	 */
	onSelectSuggestion(suggestion: any): void {

		console.log('onSelectSuggestion called with:', suggestion);

		if (!suggestion || !suggestion.placePrediction) {
			console.warn('Invalid suggestion received:', suggestion);
			return;
		}

		// Fetch place details and populate fields
		this.googlePlacesService.handleSuggestionSelected( suggestion, this.movingFrom );

		// Blur input after selection (desktop and mobile)
		setTimeout( () => {
			( document.activeElement as HTMLElement )?.blur();
			this.addressInputEl?.nativeElement.blur();
		}, 0 );
	}

	public onSubmit() {

		this.formService.submitFormGroup( (<UntypedFormGroup>this.movingFrom) );
	}

	onCountryChange( isInitialCall = false ) {

		var selectedCountry = this.movingFrom.controls['country'].value;

		// Use verifycountry here
		selectedCountry = this.formService.verifyCountryCode( selectedCountry );

		// Clean up old autocomplete instance
		this.googlePlacesService.cleanup();

		// Re-initialize Google Places Autocomplete with new country code
		this.initializeGooglePlacesAutocomplete();

		// Ireland, Qatar, United-arab-emirates, Hong kong, Curacao
		// Aruba, Cameroon, Ghana, Suriname, Sint maarten
		var excludedCountries = ['IE', 'QA', 'AE', 'HK', 'CW', 'AW', 'CM', 'GH', 'SR', 'SX'];
		var postcodeControl = (<UntypedFormGroup>this.movingFrom).controls['postcode'];

		if( excludedCountries.includes( selectedCountry ) ) {

			// Make un-required and reset value
			postcodeControl.removeValidators( Validators.required );
			postcodeControl.reset();

			// Change field order
			this.fieldOrder = ['address','city','postcode'];

			// Hide postcode field
			this.postcodeEnabled = false;
		}
		else {

			// Make required
			postcodeControl.addValidators( Validators.required );

			// Show postcode field
			this.postcodeEnabled = true;

			// Reset field order
			this.fieldOrder = this.formService.getFieldOrder();
		}

		console.log('attempt to get order from order by country', selectedCountry );

		this.fieldOrder = this.formService.getFieldOrderByCountry( selectedCountry );

		console.log( 'Field order', this.fieldOrder );

		// Check if we can enable movingToExtended
		this.formService.checkToEnableMovingToExtended( isInitialCall );
		
		// Handle state field validation based on country selection
		this.updateStateFieldValidation();
	}

	onPostcodeFieldKeyup( $event: any ) {

		// Ignore tab 9
		// Ignore shift 16
		// Ignore arrow keys 37,38,39,40
		if( [9, 16, 37, 38, 39, 40].includes( $event.keyCode ) )
			return;

		// Launch postcode API
		this.doPostcodeApi();
	}

	/**
	 * Check if the state field should be enabled based on domain setting and country selection
	 */
	public isStateFieldEnabled(): boolean {
		return this.stateFieldEnabled && this.formService.verifyCountryCode(this.movingFrom.controls['country'].value) === 'US';
	}

	/**
	 * Update state field validation based on current conditions
	 */
	private updateStateFieldValidation(): void {
		const stateControl = this.movingFrom.controls['state'];
		
		if (this.isStateFieldEnabled()) {
			// Add required validator when state field should be shown
			stateControl.addValidators(Validators.required);
		} else {
			// Remove required validator when state field should be hidden
			stateControl.removeValidators(Validators.required);
			// Reset the state value when hiding the field
			stateControl.reset();
		}
		
		// Update validation
		stateControl.updateValueAndValidity();
	}

	/**
	 * Handles the call to the postcode API and the results of it
	 * Only for NL
	 */
	public doPostcodeApi( ) {

		if( ! this.formService.getSetting('postcodeApiEnabled') || this.postcodeBusy )
			return;

		var movingFrom = this.movingFrom;

		// Get initial postcode
		var postcodeValue = movingFrom.controls['postcode'].value;

		// Clears everything except letters and numbers
		// This is meant for the Dutch postcode format
		postcodeValue = postcodeValue.replace( /[^a-zA-Z0-9]+/g, '' );

		// Check if the country is already set and on a different country than the netherlands
		var countryHasValueAndIsNotNetherlands = movingFrom.controls['country'].value && movingFrom.controls['country'].value.length > 0 && this.formService.verifyCountryCode( movingFrom.controls['country'].value ) !== 'NL';

		// Need at least 6 characters for the postcode API to start
		if( postcodeValue.length < 6 || postcodeValue.length > 7 || countryHasValueAndIsNotNetherlands )
			return;

		// To only have 1 at a time.
		this.postcodeBusy = true;

		// Pass this value to the postcode api
		var postcodeData = this.apiService.getPostCodeData( postcodeValue ).subscribe( { next: response => {

			if( response.status === 'ok' ) {

				var results = response.results[0];

				// Can only be the netherlands
				movingFrom.controls['country'].setValue( 'NL' );
				movingFrom.controls['country'].markAsDirty();

				movingFrom.controls['city'].setValue( results.city );
				movingFrom.controls['address'].setValue( results.street + ' ' );
				this.addressInputEl.nativeElement.focus();
			}

			this.postcodeBusy = false;
			postcodeData.unsubscribe();
		},
		error: error => {

			// This way it can be attempted again
			this.postcodeBusy = false;
		} } );
	}
}
